<template>
    <div class="ngdialog-content">
        <dialog-header>
            <h4>{{ $t('overview.activate_new_system') }}</h4>
        </dialog-header>

        <form
            v-if="!hasSystem"
            name="searchForm"
            novalidate
            @submit.prevent="search()"
            :class="{ 'has-error': notFound }"
        >
            <div class="ngdialog__body">
                <div class="row form__row">
                    <div class="column-mobile-24">
                        <label class="form__label" for="serial_number">{{
                            $t('fields.serial_number')
                        }}</label>
                        <input
                            class="form__field"
                            name="serial_number"
                            type="text"
                            v-model="searchFormData.serial_number"
                            required
                            @input="notFound = false"
                            :class="{
                                'is-invalid': $v.searchFormData.serial_number.$invalid,
                                'is-dirty': $v.searchFormData.serial_number.$dirty,
                            }"
                        />

                        <p class="form__error" v-if="notFound">
                            {{ $t('activation.no_system_found') }}
                        </p>
                    </div>
                </div>

                <div class="row form__row">
                    <div class="column-mobile-24">
                        <label class="form__label" for="verification_number">{{
                            $t('fields.verification_number')
                        }}</label>
                        <input
                            class="form__field"
                            name="verification_number"
                            type="text"
                            v-model="searchFormData.verification_number"
                            required
                            @input="notFound = false"
                            :class="{
                                'is-invalid': $v.searchFormData.verification_number.$invalid,
                                'is-dirty': $v.searchFormData.verification_number.$dirty,
                            }"
                        />
                    </div>
                </div>
            </div>

            <dialog-buttons :submit-text="$t('activation.start_activation_process')" />
        </form>

        <form v-if="hasSystem" name="activateForm" novalidate @submit.prevent="submit()">
            <div class="ngdialog__body">
                <p>
                    <span>{{ $t('fields.serial_number') }}</span
                    >: {{ selectedSystem.serial_number }}
                </p>

                <p>
                    <a class="button button--primary button--fluid" @click="changeSystem()">
                        {{ $t('activation.change_system') }}
                    </a>
                </p>

                <div class="alert alert-danger hide-slide-animation" v-if="showError" role="alert">
                    {{ $t('errors.error_occurred') }}
                </div>

                <system-form-base
                    ref="systemBase"
                    :name.sync="activateFormData.name"
                    :address1.sync="activateFormData.address1"
                    :address2.sync="activateFormData.address2"
                    :postcode.sync="activateFormData.postcode"
                    :city.sync="activateFormData.city"
                    :region.sync="activateFormData.region"
                    :country.sync="activateFormData.country"
                    :system_status.sync="activateFormData.system_status"
                    :boiler_image_name.sync="activateFormData.boiler_image_name"
                    :boiler_name="systemData ? systemData.boiler_name : ''"
                    :remote-errors.sync="activateRemoteErrors"
                    :available-boilers="systemData ? systemData.available_boiler_images : {}"
                />

                <div class="row form__row">
                    <input
                        type="email"
                        class="form__field"
                        name="owner_email"
                        :placeholder="$t('fields.owner_email')"
                        v-model="activateFormData.ownerEmail"
                        required
                    />

                    <div v-if="$v.activateFormData.ownerEmail.$dirty">
                        <p class="form__error" v-if="!$v.activateFormData.ownerEmail.required">
                            {{ $t('fields.owner_email_e_required') }}
                        </p>

                        <p class="form__error" v-if="!$v.activateFormData.ownerEmail.email">
                            {{ $t('fields.email_e_email') }}
                        </p>
                    </div>
                </div>
            </div>

            <dialog-buttons :submit-text="$t('activation.activate')" />
        </form>
    </div>
</template>

<script>
import * as api from '@/api';
import { required, email } from 'vuelidate/lib/validators';
import SystemFormBase from '@/components/SystemFormBase';
import DialogHeader from '@/components/DialogHeader';
import DialogButtons from '@/components/DialogButtons';

export default {
    components: {
        SystemFormBase,
        DialogHeader,
        DialogButtons,
    },
    props: {
        system: {
            type: Object,
        },
    },
    data() {
        return {
            notFound: false,
            showError: false,
            selectedSystem: this.system,
            searchFormData: {
                serial_number: '',
                verification_number: '',
            },
            activateFormData: {
                ownerEmail: '',
                name: '',
                address1: '',
                address2: '',
                postcode: '',
                city: '',
                region: '',
                country: '',
                system_status: '',
                boiler_image_name: '',
            },
            activateRemoteErrors: null,
            systemData: false,
        };
    },
    validations() {
        return {
            searchFormData: {
                serial_number: {
                    required,
                },
                verification_number: {
                    required,
                },
            },
            activateFormData: {
                ownerEmail: {
                    required,
                    email,
                },
            },
        };
    },
    computed: {
        hasSystem() {
            return !!this.selectedSystem;
        },
    },
    mounted() {
        if (this.system) {
            this.loadSystemById(this.system.id);
        }
    },
    methods: {
        async loadSystemById(id) {
            try {
                const response = await api.systems.get({ id });
                this.systemData = response.data
            } catch (error) {
                console.error(error);
            }
        },
        changeSystem() {
            this.selectedSystem = null;

            // reset activateFormData and searchFormData
            let initialData = this.$options.data.apply(this);
            Object.assign(this.activateFormData, initialData.activateFormData);
            Object.assign(this.searchFormData, initialData.searchFormData);
        },
        search() {
            this.notFound = false;

            if (this.$v.searchFormData.$invalid) {
                this.$v.searchFormData.$touch();
                return;
            }

            api.systems
                .getBySerialNumber(
                    { serialNumber: this.searchFormData.serial_number },
                    { verification_number: this.searchFormData.verification_number }
                )
                .then(
                    response => {
                        this.notFound = false;
                        this.selectedSystem = response.data;
                        this.loadSystemById(this.selectedSystem.id);
                    },
                    () => {
                        this.notFound = true;
                    }
                );
        },
        submit() {
            this.activateRemoteErrors = null;

            if (this.$v.activateFormData.$invalid || this.$refs.systemBase.$v.$invalid) {
                this.$v.activateFormData.$touch();
                this.$refs.systemBase.$v.$touch();
                return;
            }

            api.systems
                .activate({
                    id: this.selectedSystem.id,
                    system: this.activateFormData,
                    ownerEmail: this.activateFormData.ownerEmail,
                })
                .then(
                    () => {
                        this.showError = false;
                        this.$router.push({
                            name: 'system-management',
                            params: { id: this.selectedSystem.id },
                        });
                        this.$emit('close');
                    },
                    error => {
                        this.showError = true;
                        this.activateRemoteErrors = error.response.errors;
                    }
                );
        },
    },
};
</script>
